import { Banknote } from 'lucide-react';

/**
 * MmfCompareCard — P158·16 (Feb 2026).
 *
 * Renders in place of the generic side-by-side compare table when
 * BOTH slots are Money Market Funds. MMFs have no revenue / EPS /
 * PE / DGR / payout ratio / triangle metrics, so the standard
 * comparison table collapses to a wall of em-dashes. This card
 * shows the metrics that actually matter for MMFs: 7-day yield,
 * total net assets, family, tax category (Government / Treasury /
 * Prime / Municipal), and expense ratio when available.
 *
 * The tax category is inferred from the fund name string using a
 * simple keyword pass — no separate DB field required. Categories
 * carry very different tax implications:
 *   • Government MMF — federal-taxable, state-taxable on non-Treasury slice
 *   • Treasury MMF   — federal-taxable, state-exempt on direct Treasuries
 *   • Municipal MMF  — federal-exempt (single-state versions also state-exempt)
 *   • Prime MMF      — federal + state taxable, small credit risk
 */
function inferMmfCategory(name = '') {
  const n = (name || '').toLowerCase();
  if (n.includes('municipal') || n.includes('tax-free') || n.includes('tax free') || n.includes('tax exempt') || n.includes('tax-exempt')) return { label: 'Municipal', tax: 'Federal-exempt (state-exempt for single-state residents)' };
  if (n.includes('treasury')) return { label: 'Treasury', tax: 'Federal-taxable, state-exempt on direct Treasuries' };
  if (n.includes('prime')) return { label: 'Prime', tax: 'Federal + state taxable, small credit risk' };
  if (n.includes('government') || n.includes('u.s. gov') || n.includes('us govt')) return { label: 'Government', tax: 'Federal-taxable, mixed state treatment' };
  return { label: 'Money Market', tax: 'Federal + state taxable (ordinary income)' };
}

function fmtAum(n) {
  if (typeof n !== 'number' || !Number.isFinite(n)) return '—';
  if (n >= 1e9) return `$${(n / 1e9).toFixed(2)}B`;
  if (n >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
  return `$${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

function fmtPct(n) { return typeof n === 'number' ? `${n.toFixed(2)}%` : '—'; }

function Row({ label, a, b, aBetter, bBetter, help }) {
  return (
    <div className="grid grid-cols-[1fr_1fr_1fr] gap-2 px-3 py-2.5 border-t border-border/60">
      <div className="text-xs font-semibold text-muted-foreground self-center">
        {label}
        {help ? <div className="text-[10px] font-normal text-muted-foreground/70 mt-0.5">{help}</div> : null}
      </div>
      <div className={`text-sm font-mono tabular-nums ${aBetter ? 'font-bold text-emerald-600 dark:text-emerald-400' : ''}`}>{a}</div>
      <div className={`text-sm font-mono tabular-nums ${bBetter ? 'font-bold text-emerald-600 dark:text-emerald-400' : ''}`}>{b}</div>
    </div>
  );
}

export default function MmfCompareCard({ aDetail, bDetail }) {
  if (!aDetail || !bDetail || !aDetail.is_money_market_fund || !bDetail.is_money_market_fund) return null;

  const aYield = typeof aDetail.mmf_7day_yield === 'number' ? aDetail.mmf_7day_yield : null;
  const bYield = typeof bDetail.mmf_7day_yield === 'number' ? bDetail.mmf_7day_yield : null;
  const aAum = typeof aDetail.mmf_aum === 'number' ? aDetail.mmf_aum : null;
  const bAum = typeof bDetail.mmf_aum === 'number' ? bDetail.mmf_aum : null;
  const aCat = inferMmfCategory(aDetail.mmf_category || aDetail.name || aDetail.long_name);
  const bCat = inferMmfCategory(bDetail.mmf_category || bDetail.name || bDetail.long_name);
  const aExp = typeof aDetail.mmf_expense_ratio === 'number' ? aDetail.mmf_expense_ratio : null;
  const bExp = typeof bDetail.mmf_expense_ratio === 'number' ? bDetail.mmf_expense_ratio : null;

  // Winner marks: higher yield / lower expense / larger AUM = better.
  const yWin = (aYield ?? -1) === (bYield ?? -1) ? 'tie' : (aYield ?? -1) > (bYield ?? -1) ? 'a' : 'b';
  const eWin = aExp === null || bExp === null ? 'tie' : aExp === bExp ? 'tie' : aExp < bExp ? 'a' : 'b';
  const aWin = (aAum ?? -1) === (bAum ?? -1) ? 'tie' : (aAum ?? -1) > (bAum ?? -1) ? 'a' : 'b';

  return (
    <div className="rounded-xl border-2 border-cyan-500/40 bg-cyan-50/40 dark:bg-cyan-950/20 overflow-hidden" data-testid="mmf-compare-card">
      <div className="px-3 py-2.5 border-b border-cyan-500/30 flex items-center gap-2">
        <Banknote className="w-4 h-4 text-cyan-600 dark:text-cyan-300" />
        <h3 className="text-[11px] font-bold uppercase tracking-widest text-cyan-700 dark:text-cyan-300">
          Money Market Fund Comparison
        </h3>
      </div>
      <Row
        label="Ticker"
        a={<span className="font-bold text-cyan-800 dark:text-cyan-200">{aDetail.ticker}</span>}
        b={<span className="font-bold text-cyan-800 dark:text-cyan-200">{bDetail.ticker}</span>}
      />
      <Row
        label="Fund family"
        a={<span className="text-xs">{aDetail.mmf_family_label || aDetail.mmf_family || '—'}</span>}
        b={<span className="text-xs">{bDetail.mmf_family_label || bDetail.mmf_family || '—'}</span>}
      />
      <Row
        label="Category"
        a={<span className="text-xs font-semibold">{aCat.label}</span>}
        b={<span className="text-xs font-semibold">{bCat.label}</span>}
        help="Derived from the fund's SEC filing name"
      />
      <Row
        label="7-Day Yield"
        a={fmtPct(aYield)}
        b={fmtPct(bYield)}
        aBetter={yWin === 'a'} bBetter={yWin === 'b'}
        help="Annualised past-week net yield"
      />
      <Row
        label="Total Net Assets"
        a={fmtAum(aAum)}
        b={fmtAum(bAum)}
        aBetter={aWin === 'a'} bBetter={aWin === 'b'}
        help="Larger AUM ≈ more liquidity"
      />
      <Row
        label="Expense Ratio"
        a={fmtPct(aExp)}
        b={fmtPct(bExp)}
        aBetter={eWin === 'a'} bBetter={eWin === 'b'}
        help="Lower is better — drags on the yield"
      />
      <div className="grid grid-cols-[1fr_1fr_1fr] gap-2 px-3 py-2.5 border-t border-border/60">
        <div className="text-xs font-semibold text-muted-foreground self-center">Tax treatment</div>
        <div className="text-[11px] leading-relaxed">{aCat.tax}</div>
        <div className="text-[11px] leading-relaxed">{bCat.tax}</div>
      </div>
      <div className="px-3 py-2.5 text-[11px] text-muted-foreground bg-amber-50 dark:bg-amber-950/30 border-t border-amber-300/60 dark:border-amber-700/40">
        <strong>Reminder:</strong> both funds aim to maintain a constant $1.00 NAV but are not guaranteed to do so and are not FDIC-insured. Not investment or tax advice.
      </div>
    </div>
  );
}
